import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import isEqual from "lodash-es/isEqual";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import TextLink from "components/DocumentationSnippets/TextLink";
import FormLoading from "components/form/components/loading";
import FormFieldBlobFolderPrefix from "components/FormFields/BlobFolderPrefix";
import FormFieldGitSparseCheckoutPaths from "components/FormFields/GitSparseCheckoutPaths";
import MissingDataBanner from "components/MissingDataBanner";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import TileCheckboxGrid from "components/TileCheckboxGrid";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import IconEmphasized from "ds/components/IconEmphasized";
import Input from "ds/components/Input";
import Tile from "ds/components/Tile";
import TileContent from "ds/components/Tile/Content";
import TileTitle from "ds/components/Tile/Title";
import TileWrapper from "ds/components/Tile/Wrapper";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { useFormValidations } from "hooks/useFormValidations";
import useTypedContext from "hooks/useTypedContext";
import { VcsProvider } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import { checkWithMultipleVCSIntegrations } from "utils/vcs";
import { INTEGRATIONS_FILTER_PATH } from "views/Account/Integrations/registry/shared/VcsShared/constants";
import { AccountContext } from "views/AccountWrapper";
import Card from "ds/components/Card";

import { StackFormContext } from "../context";
import NewStackFooter from "../Footer";
import { StackCreationWizardStep, StackVcsFormFields } from "../types";
import useErrorHandlerNewStack from "../useErrorHandlerNewStack";
import useStackCreationAnalyticsVersion from "../useStackCreationAnalyticsVersion";
import { getTooltipAnalyticsProps } from "../utils";
import VcsBranchesField from "./BranchesField";
import Documentation from "./Documentation";
import { VCS_PROVIDERS_IN_SPACE } from "./gql";
import VcsIntegrationField from "./IntegrationField";
import ProjectGlobsField from "./ProjectGlobsField";
import VcsRepositoryField from "./RepositoryField";
import VcsRepositoryUrlField from "./RepositoryUrlField";
import { useVcsProviderConfig } from "./useVcsConfig";

const processValuesBeforeSubmit = (values: StackVcsFormFields): StackVcsFormFields => {
  const projectRoot = values.projectRoot?.trim();

  const additionalProjectGlobs = values.additionalProjectGlobs
    ?.map((projectGlob) => {
      return {
        value: projectGlob.value.trim(),
      };
    })
    .filter((projectGlob) => projectGlob.value);

  const gitSparseCheckoutPaths = values.gitSparseCheckoutPaths
    ?.map((path) => {
      return {
        value: path.value.trim(),
      };
    })
    .filter((path) => path.value);

  return {
    ...values,
    projectRoot,
    additionalProjectGlobs,
    gitSparseCheckoutPaths,
  };
};

const NewStackVcs = () => {
  const { viewer } = useTypedContext(AccountContext);
  const analyticsVersion = useStackCreationAnalyticsVersion();

  const { currentStep, updateStepData, formData, setInternalFormData, internalData } =
    useTypedContext(StackFormContext);
  const stackDetailsStepData = formData[StackCreationWizardStep.Details];
  const stepData = formData[StackCreationWizardStep.Vcs];

  const builderForm = useForm<StackVcsFormFields>({
    defaultValues: {
      ...stepData,
    },
    mode: "onChange",
  });

  const { control, setValue, register, watch, reset } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const { data, loading, error, refetch, networkStatus } = useQuery(VCS_PROVIDERS_IN_SPACE, {
    variables: {
      spaceId: stackDetailsStepData.space,
    },
    skip: !stackDetailsStepData.space,
  });

  useErrorHandlerNewStack(error);

  const newStepData = watch();

  const processStepData = () => {
    const preparedData = processValuesBeforeSubmit(newStepData);
    return updateStepData(currentStep, preparedData);
  };

  const isDataChanged = !isEqual(
    {
      ...newStepData,
      additionalProjectGlobs: newStepData.additionalProjectGlobs?.filter(({ value }) => value),
      gitSparseCheckoutPaths: newStepData.gitSparseCheckoutPaths?.filter(({ value }) => value),
    },
    stepData
  );

  const vcsProviderConfig = useVcsProviderConfig(data?.providersInSpace);

  const withMultipleVCSIntegrations = checkWithMultipleVCSIntegrations(newStepData.provider);

  const waitForVCSIntegrationId = withMultipleVCSIntegrations && !newStepData.vcsIntegrationId;

  const handleChangeProvider = (fieldValue: VcsProvider | undefined, type: VcsProvider) => () => {
    if (type !== fieldValue) {
      reset({
        repository: "",
        branch: "",
        projectRoot: "",
        repositoryName: "",
        repositoryURL: undefined,
        provider: type,
        additionalProjectGlobs: [],
        vcsIntegrationId: undefined,
        gitSparseCheckoutPaths: [],
      });

      setInternalFormData((state) => ({
        ...state,
        vcsIntegrationLabel: undefined,
        useGitCheckout: undefined,
      }));
    }
  };

  // Show the git sparse checkout option when:
  // - the provider is Git
  // - the provider is Github and it uses git sparse checkout
  // - the internal data has useGitCheckout enabled (for other providers)

  const shouldShowGitSparseCheckout =
    newStepData.provider !== VcsProvider.Spacelift &&
    (internalData.useGitCheckout ||
      newStepData.provider === VcsProvider.Git ||
      (newStepData.provider === VcsProvider.Github && data?.githubIntegration?.useGitCheckout) ||
      (newStepData.provider === VcsProvider.Origin && data?.originIntegration?.useGitCheckout));

  useEffect(() => {
    if (!newStepData.provider) {
      setValue("provider", vcsProviderConfig[0]?.type);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vcsProviderConfig]);

  if (loading && networkStatus !== NetworkStatus.refetch) {
    return <FormLoading />;
  }

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Connect to source code"
          description={
            <>
              {viewer?.admin && (
                <>
                  Spacelift supports multiple source code providers. You can connect one on the{" "}
                  <TextLink
                    analyticsPage={AnalyticsPageStack.StackNew}
                    analyticsProps={{ version: analyticsVersion }}
                    analyticsTitle="Source code link click"
                    href={INTEGRATIONS_FILTER_PATH}
                  >
                    Source Code Integrations
                  </TextLink>{" "}
                  page.
                </>
              )}
              {!viewer?.admin && (
                <>
                  Spacelift supports multiple source code providers. <br />
                  Please reach out to one of your friendly account admins to set up one.
                </>
              )}
            </>
          }
        />

        {!data && (
          <Box direction="column">
            <MissingDataBanner
              refreshLoading={loading && networkStatus === NetworkStatus.refetch}
              refreshHandler={refetch}
            />
          </Box>
        )}

        {data && (
          <FormProvider {...builderForm}>
            <Controller
              name="provider"
              control={control}
              render={({ field }) => (
                <Box direction="column" gap="x-large">
                  <TileCheckboxGrid>
                    {vcsProviderConfig.map(({ name, logo, type }) =>
                      vcsProviderConfig.length === 1 ? (
                        <TileWrapper key={type}>
                          <TileContent direction="row" align="center">
                            <IconEmphasized icon={logo} />
                            <TileTitle>{name}</TileTitle>
                          </TileContent>
                        </TileWrapper>
                      ) : (
                        <Tile
                          key={type}
                          selected={field.value === type}
                          title={name}
                          icon={logo}
                          onClick={handleChangeProvider(field.value, type)}
                          indicator="radio"
                        />
                      )
                    )}
                  </TileCheckboxGrid>
                </Box>
              )}
            />
            <Box direction="column" margin="x-large 0 large 0" gap="large">
              {newStepData.provider === VcsProvider.Spacelift && (
                <Banner variant="info">
                  Spacelift Repos doesn't support branches. Your stack will always track the latest
                  commit.
                </Banner>
              )}
              {newStepData.provider &&
                newStepData.provider !== VcsProvider.Git &&
                newStepData.provider !== VcsProvider.Blob &&
                !waitForVCSIntegrationId && (
                  <Banner variant="info">
                    Repositories without any commits are not shown in this list.
                  </Banner>
                )}

              {withMultipleVCSIntegrations &&
                newStepData.provider &&
                stackDetailsStepData.space && (
                  <VcsIntegrationField
                    provider={newStepData.provider}
                    spaceId={stackDetailsStepData.space}
                  />
                )}

              {newStepData.provider &&
                newStepData.provider !== VcsProvider.Git &&
                newStepData.provider !== VcsProvider.Blob &&
                !waitForVCSIntegrationId && <VcsRepositoryField />}
              {newStepData.provider === VcsProvider.Git && <VcsRepositoryUrlField />}
              {newStepData.provider === VcsProvider.Blob && !waitForVCSIntegrationId && (
                <FormFieldBlobFolderPrefix />
              )}
              {newStepData.repository &&
                !waitForVCSIntegrationId &&
                newStepData.provider !== VcsProvider.Spacelift &&
                newStepData.provider !== VcsProvider.Blob && <VcsBranchesField />}

              <FormField
                label="Project root"
                isOptional
                {...getTooltipAnalyticsProps("Source Code", "Project root", {
                  provider: newStepData.provider,
                  version: analyticsVersion,
                })}
                tooltipInfoVariant="modal"
                tooltipInfo={
                  <>
                    <TooltipModalTitle>Project root</TooltipModalTitle>
                    <TooltipModalBody align="start">
                      Project root is the optional directory relative to the workspace root
                      containing the entrypoint to the Stack.
                      <ReadMoreDocsLink
                        docsUrl={getDocsUrl("/concepts/stack/stack-settings#project-root")}
                      />
                    </TooltipModalBody>
                  </>
                }
                noMargin
              >
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Defaults to root of the repo"
                    {...register("projectRoot")}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
              <Card variant="secondary" contentGap="large">
                <ProjectGlobsField />
              </Card>
              {shouldShowGitSparseCheckout && (
                <Card variant="secondary" contentGap="large">
                  <FormFieldGitSparseCheckoutPaths />
                </Card>
              )}
            </Box>
          </FormProvider>
        )}
      </FullScreenModalBody>
      <NewStackFooter
        isDataChanged={isDataChanged}
        processStepData={processStepData}
        documentationLink={getDocsUrl("/concepts/stack/creating-a-stack#2-connect-to-source-code")}
        documentationTitle="Integrate source code"
        documentationBody={<Documentation />}
        runFormValidations={runFormValidations}
      />
    </>
  );
};

export default NewStackVcs;
